import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Mail, ArrowRightLeft, Star, AlertOctagon, Plane, ShieldAlert, FileWarning, FileCheck2, CalendarClock } from "lucide-react";
import { API } from "@/App";

const NotificationContext = createContext(null);
export const useNotifications = () => useContext(NotificationContext);

const ICONS = {
  ticket_new: Ticket,
  ticket_new_comment: Mail,
  ticket_transferred: ArrowRightLeft,
  ticket_assigned: Ticket,
  survey_submitted: Star,
  ticket_auto_closed: AlertOctagon,
  viajero_trip_inicio: CalendarClock,
  viajero_trip_en_curso: Plane,
  viajero_trip_concluido: FileCheck2,
  viajero_seguro_pendiente: ShieldAlert,
  viajero_docs_pendientes: FileWarning,
  viajero_vuelo_cambiado: Plane,
  viajero_doc_por_vencer: FileWarning,
  viajero_doc_renovado: FileCheck2,
};

const TITLES = {
  ticket_new: "Nuevo ticket",
  ticket_new_comment: "Nuevo mensaje en ticket",
  ticket_transferred: "Ticket transferido",
  ticket_assigned: "Ticket asignado a ti",
  survey_submitted: "Encuesta respondida",
  ticket_auto_closed: "Ticket cerrado automáticamente",
  viajero_trip_inicio: "Viaje próximo a iniciar",
  viajero_trip_en_curso: "Viaje en curso",
  viajero_trip_concluido: "Viaje concluido",
  viajero_seguro_pendiente: "Seguro de viaje pendiente",
  viajero_docs_pendientes: "Documentos de viaje pendientes",
  viajero_vuelo_cambiado: "Cambio de vuelos",
  viajero_doc_por_vencer: "Documento por vencer",
  viajero_doc_renovado: "Documento renovado",
};

const formatMsg = (payload) => {
  switch (payload.type) {
    case "ticket_new": return `${payload.folio} · ${payload.title}`;
    case "ticket_new_comment": return `${payload.folio} — ${payload.from}: ${payload.preview || ""}`.slice(0, 180);
    case "ticket_transferred": return `${payload.folio} · ${payload.from || "—"} → ${payload.to}`;
    case "ticket_assigned": return `${payload.folio} · ${payload.title}`;
    case "survey_submitted": return `${payload.folio} · ⭐ Resp ${payload.response_rating}/5 · ⭐ Atención ${payload.attention_rating}/5`;
    case "ticket_auto_closed": return `${payload.folio} · ${payload.title}`;
    default: return payload.message || payload.title || "Notificación";
  }
};

export const NotificationProvider = ({ children, user }) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);

  const handleNotification = useCallback((payload) => {
    if (!payload || !payload.type) return;
    setHistory((prev) => [{ ...payload, _ts: Date.now(), _id: `${Date.now()}-${Math.random()}` }, ...prev].slice(0, 50));

    const Icon = ICONS[payload.type] || Ticket;
    const title = TITLES[payload.type] || "Notificación";
    const msg = formatMsg(payload);
    const ticketId = payload.ticket_id;

    toast.custom((t) => (
      <div
        className="bg-white border border-slate-200 shadow-xl rounded-lg p-4 flex items-start gap-3 cursor-pointer hover:border-blue-300 hover:shadow-2xl transition-all w-[360px]"
        onClick={() => {
          toast.dismiss(t);
          if (ticketId) navigate(`/helpdesk/tickets/${ticketId}`);
          else if (payload.route) navigate(payload.route);
        }}
        data-testid={`notif-toast-${payload.type}`}
      >
        <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="text-xs text-slate-600 truncate">{msg}</p>
          {(ticketId || payload.route) && (
            <p className="text-[11px] text-blue-600 mt-1">Click para abrir →</p>
          )}
        </div>
      </div>
    ), { duration: 10000 });
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    let destroyed = false;
    let es = null;
    let reconnectTimer = null;

    const connect = () => {
      if (destroyed) return;
      try {
        es = new EventSource(`${API}/sse/notifications`, { withCredentials: true });
        es.onmessage = (e) => {
          try { handleNotification(JSON.parse(e.data)); } catch { /* ignore */ }
        };
        es.onerror = () => {
          try { es.close(); } catch { /* ignore */ }
          if (destroyed) return;
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, 5000);
        };
      } catch {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(connect, 5000);
      }
    };

    connect();
    return () => {
      destroyed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (es) {
        try { es.close(); } catch { /* ignore */ }
      }
    };
  }, [user, handleNotification]);

  return (
    <NotificationContext.Provider value={{ history, clear: () => setHistory([]) }}>
      {children}
    </NotificationContext.Provider>
  );
};
